import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { DataGrid as DxDataGrid } from 'devextreme-react/data-grid';
import type {
  Action,
  ActionEvent,
  ActionFn,
  Filter,
  SetIsLoading,
} from '#/common/components/DataGrid';
import DataGrid from '#/common/components/DataGrid';
import columns from './columns';
import { gql } from '@apollo/client';
import { getWorkOrderList } from '#/graphql/queries';
import useToast from '#/common/hooks/useToast';
import useWorkOrderDelete from '../../hooks/useWorkOrderDelete';
import { states } from '../../mappings/mappings';
import { WorkOrderItem } from '#/graphql/types';
import { useSearchParams } from 'react-router-dom';
import { actions } from './actions';
import useWorkOrderBulkUpdate from '../../hooks/useWorkOrderBulkUpdate';
import ModalWithForm from '#/common/components/ModalWithForm';
import useSession from '#/common/hooks/useSession';
import {
  ACTION_STATE_RULES,
  BULK_EDIT_MENU_OPTIONS,
  BulkEditMenu,
  determineNextState,
  getEnhancedMenuItems,
  getNotEligibleContent,
  getSuccessMessage,
} from '#/modules/workOrder/utils/bulk-edit';
import { UserRole } from '#/common/types/userTypes';
import ToolbarButtonPopup from '#/common/components/ToolbarButtonPopup';
import { getElementPosition } from '#/common/components/DataGrid/utils';
import getPermanentFilterValue from '#/common/utils/parsers/parsePermanentFilters';
import { useRedirectToRelatedFindingsConfirm } from '../../hooks/useRedirectToRelatedFindingsConfirm';
import SimplePopup from '#/common/components/SimplePopup';
import { merge } from 'lodash';
import hasTypedKey from '#/common/utils/typeGuards/hasTypedKey';
import type { FormItem } from '#/common/components/Form';

const getWorkOrderListQuery = gql(getWorkOrderList);

interface WorkOrderDataGridProps {
  omitColumns?: string[];
  hideNewAction?: boolean;
  permanentFilters?: Filter[];
  hideBulkActions?: boolean;
  toolbarPrefix?: React.ReactNode;
  visibleColumns?: string[];
  gridId?: string;
}

const WorkOrderDataGrid: React.FC<WorkOrderDataGridProps> = ({
  omitColumns,
  hideNewAction,
  permanentFilters,
  hideBulkActions,
  toolbarPrefix,
  visibleColumns,
  gridId = 'work-orders',
}) => {
  const { confirm, alert } = useToast();
  const { deleteWorkOrder } = useWorkOrderDelete();

  const { redirectToRelatedFindingsConfirm } = useRedirectToRelatedFindingsConfirm();

  const [searchParams] = useSearchParams();
  const dgRef = useRef<DxDataGrid | null>(null);
  const assetId =
    searchParams.get('assetId') ||
    getPermanentFilterValue('assetId', permanentFilters) ||
    undefined;
  const tenantId =
    searchParams.get('tenantId') ||
    getPermanentFilterValue('tenantId', permanentFilters) ||
    undefined;

  const [eligibleCount, setEligibleCount] = useState(0);

  // global eligibility helper (Closed is never eligible)
  const filterEligibleForAction = useCallback((actionId: string, items: WorkOrderItem[]) => {
    const notClosed = items.filter((wo) => wo.state !== states.closed.key);
    const rule = ACTION_STATE_RULES[actionId];
    return rule ? notClosed.filter((wo) => rule(wo?.state ?? '')) : notClosed;
  }, []);

  const woActions: Action<WorkOrderItem>[] = useMemo(() => {
    if (hideBulkActions) {
      return [...actions];
    }
    return [
      ...actions,
      {
        key: 'bulk-edit-work-orders',
        icon: 'edit_note',
        text: 'Bulk Edit',
        showTextOnly: true,
        disabled: (e: any) => !e.selectedItems?.length,
        onClick: (e: ActionEvent<WorkOrderItem>) => {
          const { buttonRef, selectedItems } = e;
          if (!buttonRef || !selectedItems?.length) return;

          const { bottom = 0, left = 0 } = getElementPosition(buttonRef.current);
          setPostion({ bottom, left });
          setShowToolbarPopup(true);
          setSelectedItems(selectedItems);
        },
      },
    ];
  }, [assetId, tenantId, hideNewAction, hideBulkActions]);

  const deleteItem = useCallback(
    async (workOrders: WorkOrderItem[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      const workOrdersToDelete = workOrders.filter((wo) => wo.state === states.open.key);

      if (!workOrdersToDelete || workOrdersToDelete.length === 0) {
        return undefined;
      }

      if (workOrdersToDelete.length < workOrders.length) {
        const sure = await confirm(
          'error',
          `Some selected work orders are not in 'Open' state and can not be deleted.`,
        );
        if (!sure) return;
      }

      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteWorkOrder(workOrdersToDelete.map((a) => a.id).join(','), assetId);
      setIsLoading && setIsLoading(false);
    },
    [deleteWorkOrder, assetId],
  );

  const workOrderSelectedActions: ActionFn<WorkOrderItem>[] = useMemo(
    () =>
      hideBulkActions
        ? []
        : [
            {
              key: 'delete-work-orders',
              icon: 'trash',
              text: 'Delete',
              disabled: (e) => {
                const filteredKeysForDelete = e.selectedItems.filter(
                  (wo) => wo.state === states.open.key,
                );
                const count = filteredKeysForDelete?.length;
                return count < 1;
              },
              onClick: async (e) => {
                await deleteItem(e.selectedItems, { setIsLoading: e.setIsLoading });

                e.dg?.instance.clearSelection();
                e.dg?.instance.refresh();
              },
            },
          ],
    [hideBulkActions, deleteItem],
  );

  const cols = useMemo(() => {
    let filteredColumns = columns.filter((c) => !(omitColumns || []).includes(c.dataField));

    // If visibleColumns is provided, set visibility based on it
    if (visibleColumns && visibleColumns.length > 0) {
      filteredColumns = filteredColumns.map((col) => ({
        ...col,
        visible: visibleColumns.includes(col.dataField),
      }));
    }

    return filteredColumns;
  }, [omitColumns, visibleColumns]);

  const { user } = useSession();
  const role = user?.role as UserRole | undefined;
  const menuItems = BULK_EDIT_MENU_OPTIONS?.[role as keyof BulkEditMenu] || [];

  const { bulkUpdateWorkOrder } = useWorkOrderBulkUpdate();

  const [position, setPostion] = useState<{ bottom?: number; left?: number }>({
    bottom: 0,
    left: 0,
  });

  const [selectedItems, setSelectedItems] = useState<WorkOrderItem[]>([]);
  const [showToolbarPopup, setShowToolbarPopup] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupContent, setErrorPopupContent] = useState<{
    title: string | null;
    message: string | null;
  }>({
    title: '',
    message: '',
  });

  const handleOnClose = useCallback(() => {
    setShowToolbarPopup(false);
  }, []);

  const enhancedMenuItems = getEnhancedMenuItems(
    menuItems,
    dgRef.current?.instance.getSelectedRowsData() || [],
  );

  const handleOnSelected = useCallback(
    async (id: string, isEligible?: boolean) => {
      if (!isEligible) {
        const { title, message } = getNotEligibleContent(id);
        setShowErrorPopup(true);
        setErrorPopupContent({ title, message });
        return;
      }

      if (id === 'assign_to_me') {
        const selectedRows = dgRef.current?.instance.getSelectedRowsData() || [];
        const eligible = filterEligibleForAction(id, selectedRows);

        if (!eligible.length) {
          alert('warning', 'No eligible work orders for this action.');
          setShowToolbarPopup(false);
          return;
        }

        const sure = await confirm(
          'info',
          `Are you sure you want to assign yourself to ${eligible?.length ?? 0} selected work orders?`,
        );
        if (!sure) return;

        try {
          const res = await bulkUpdateWorkOrder({
            values: {
              assignedToId: user?.id ?? '',
            },
            ids: eligible.map((wo) => wo.id) as string[],
          });

          dgRef.current && dgRef.current.instance.refresh();
          alert(
            'success',
            `${res?.updatedCount ?? 0} work orders were successfully assigned to you.`,
          );
        } catch (error) {
          console.error(error);
          alert('error', 'Bulk operation has failed.');
        }

        setShowToolbarPopup(false);
        return;
      }

      // Compute eligibility
      const selectedRows = dgRef.current?.instance.getSelectedRowsData() || [];
      const eligible = filterEligibleForAction(id, selectedRows);

      setEligibleCount(eligible?.length);
      setSelectedOptionId(id);
      setShowToolbarPopup(false);
    },
    [selectedItems, user, filterEligibleForAction],
  );

  const handleOnCloseFormPopup = useCallback(() => {
    setSelectedOptionId(null);
  }, []);

  const handleSubmit = useCallback(
    async (formData?: WorkOrderItem) => {
      if (!formData || !selectedItems?.length) return;

      // Determine eligibility based on selectedOptionId
      const selectedRows = dgRef.current?.instance.getSelectedRowsData() || [];
      const eligibleItems = filterEligibleForAction(selectedOptionId ?? '', selectedRows);
      const eligibleIds = eligibleItems.map((wo) => wo.id) as string[];

      if (eligibleIds.length === 0) {
        alert('warning', 'No eligible work orders for this action.');
        return;
      }

      const state = determineNextState(selectedOptionId ?? '');

      try {
        const values =
          selectedOptionId === 'close_work_orders'
            ? {
                ...formData,
                state,
                closedById: user?.id ?? '',
                closedAt: formData?.closedAt ?? new Date(),
              }
            : state
              ? { ...formData, state }
              : formData;

        const res = await bulkUpdateWorkOrder({
          values,
          ids: eligibleIds,
        });

        const successMessage = getSuccessMessage(selectedOptionId ?? '', res?.updatedCount ?? 0);

        dgRef.current?.instance.refresh();
        handleOnCloseFormPopup();

        const relatedFindingsIds = eligibleItems
          .filter((wo) => !!wo.findingId)
          .map((wo) => wo.findingId) as string[];

        if (
          selectedOptionId === 'close_work_orders' &&
          relatedFindingsIds?.length &&
          eligibleIds?.length
        ) {
          await redirectToRelatedFindingsConfirm(relatedFindingsIds, eligibleIds.length);
        }

        await alert('success', successMessage);
      } catch (error) {
        console.error(error);
        alert('error', 'Bulk operation has failed.');
      }
    },
    [selectedItems, selectedOptionId, filterEligibleForAction],
  );

  const selectedMenuItem = useMemo(() => {
    const item = enhancedMenuItems.find(({ id }) => id === selectedOptionId);
    if (!item?.fields?.length) return item;

    const assetIds = [
      ...new Set(selectedItems.map((wo) => wo.assetId).filter(Boolean)),
    ] as string[];
    const accessTo = assetIds.length ? { resource: 'assets', ids: assetIds } : undefined;

    return {
      ...item,
      fields: item.fields.map((section) => {
        const hasItems = hasTypedKey(section, 'items', 'array');
        if (!hasItems) return section;

        return {
          ...section,
          items: (section.items as FormItem<any>[]).map((field) => {
            if (!('dataField' in field) || field.dataField !== 'assignedToId') return field;

            return merge({}, field, {
              editorOptions: {
                dataStoreOptions: { accessTo },
              },
            });
          }),
        };
      }),
    };
  }, [enhancedMenuItems, selectedOptionId, selectedItems]);

  return (
    <>
      <DataGrid
        setRef={(ref) => (dgRef.current = ref)}
        id={gridId}
        pluralText="Work Orders"
        singularText="Work Order"
        columns={cols}
        recordUrl="/work-orders/:id"
        actions={woActions}
        getItemsQuery={getWorkOrderListQuery}
        selectedActions={workOrderSelectedActions}
        selectionMode="multiple"
        permanentFilters={permanentFilters}
        toolbarPrefix={toolbarPrefix}
      />

      <ToolbarButtonPopup
        top={position.bottom}
        left={position.left}
        menuItems={enhancedMenuItems}
        visible={showToolbarPopup}
        onClose={handleOnClose}
        onSelected={handleOnSelected}
      />
      <ModalWithForm
        selectedMenuItem={selectedMenuItem}
        visible={!!selectedOptionId}
        onClose={handleOnCloseFormPopup}
        onSubmit={handleSubmit}
        eligibleCount={eligibleCount}
        selectedCount={selectedItems?.length}
      />

      <SimplePopup
        width="440px"
        visible={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        onSubmit={() => setShowErrorPopup(false)}
        title={errorPopupContent.title}
        message={errorPopupContent.message}
      />
    </>
  );
};

export default React.memo(WorkOrderDataGrid);
